import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCurriculum, getTrainingProgress, getRepAttempts, getCourses, createCourse, updateCourse, deleteCourse, setLevelCourse, createLevel, updateLevel, deleteLevel, createLesson, updateLesson, deleteLesson, createQuiz, deleteQuiz, seedTraining, importCprSteps, getVideoLibrary, createVideoSection, deleteVideoSection, uploadTrainingVideo, deleteTrainingVideo } from '../utils/api';
import toast from 'react-hot-toast';

const GREEN = '#0d1f3b';
const GOLD = '#C9922A';
const blankQ = () => ({ type: 'mc', prompt: '', options: ['', ''], correct_answer: '', accepted: '' });

export default function TrainingAdmin() {
  const [levels, setLevels] = useState([]);
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState(null);
  const [repAttempts, setRepAttempts] = useState(null);
  const [courseForm, setCourseForm] = useState({ title: '', description: '' });
  const [editCourse, setEditCourse] = useState(null);   // {id,title,description}
  const [loading, setLoading] = useState(true);
  const [levelForm, setLevelForm] = useState({ title: '', description: '', passing_score: 80, sort_order: 0, course_id: '' });
  const [lessonForm, setLessonForm] = useState({ level_id: '', title: '', type: 'text', content: '', audio_url: '', cheatsheet: '', sort_order: 0, audioFile: null });
  const [quizForm, setQuizForm] = useState({ level_id: '', title: 'Step Quiz', passing_score: 80, questions: [blankQ()] });
  const [videoSections, setVideoSections] = useState([]);
  const [sectionTitle, setSectionTitle] = useState('');
  const [videoForm, setVideoForm] = useState({ section_id: '', title: '', url: '', file: null });
  const [uploadPct, setUploadPct] = useState(null);
  /* Rule for this app: if you can see it, you can link to it.
     Tab and the open rep live in the query string. */
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const openRep = params.get('rep') ? parseInt(params.get('rep'), 10) : null;

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, String(v));
    });
    setParams(next);
  };
  const setTab = (t) => setParam({ tab: t === 'overview' ? null : t, rep: null });
  const [preview, setPreview] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editCheat, setEditCheat] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (les) => {
    setEditId(les.id);
    setEditTitle(les.title || '');
    setEditContent(les.content || '');
    setEditCheat(les.cheatsheet || '');
    setPreview(les.id);
  };
  const saveEdit = async (les) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', editTitle);
      fd.append('type', les.type || 'text');
      fd.append('content', editContent);
      fd.append('cheatsheet', editCheat);
      fd.append('sort_order', les.sort_order ?? 0);
      if (les.audio_url) fd.append('audio_url', les.audio_url);
      await updateLesson(les.id, fd);
      toast.success('Lesson saved');
      setEditId(null);
      load();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    try {
      const [d, v] = await Promise.all([getCurriculum(), getVideoLibrary().catch(() => ({ sections: [] }))]);
      setLevels(d.levels || []);
      setVideoSections(v.sections || []);
      // curriculum already returns courses; fall back to the dedicated route
      if (d.courses) setCourses(d.courses);
      else { try { const c = await getCourses(); setCourses(c.courses || []); } catch { /* optional */ } }
    }
    catch (e) { toast.error('Could not load'); }
    finally { setLoading(false); }
  }

  async function addSection() {
    if (!sectionTitle) return toast.error('Section needs a title');
    try { await createVideoSection({ title: sectionTitle }); toast.success('Section added'); setSectionTitle(''); load(); }
    catch { toast.error('Failed'); }
  }
  async function removeSection(id) {
    if (!window.confirm('Delete this section and ALL its videos?')) return;
    try { await deleteVideoSection(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  }
  async function addVideo() {
    if (!videoForm.section_id) return toast.error('Pick a section');
    if (!videoForm.title) return toast.error('Video needs a title');
    if (!videoForm.file && !videoForm.url) return toast.error('Choose a video file or paste a link');
    const fd = new FormData();
    fd.append('section_id', videoForm.section_id);
    fd.append('title', videoForm.title);
    if (videoForm.url) fd.append('url', videoForm.url);
    if (videoForm.file) fd.append('video', videoForm.file);
    try {
      setUploadPct(0);
      await uploadTrainingVideo(fd, e => { if (e.total) setUploadPct(Math.round(e.loaded / e.total * 100)); });
      toast.success('Video added');
      setVideoForm(f => ({ ...f, title: '', url: '', file: null }));
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Upload failed'); }
    finally { setUploadPct(null); }
  }
  async function removeVideo(id) {
    if (!window.confirm('Delete this video?')) return;
    try { await deleteTrainingVideo(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  }

  useEffect(() => {
    if (!openRep) { setRepAttempts(null); return; }
    let live = true;
    getRepAttempts(openRep).then(d => { if (live) setRepAttempts(d); }).catch(() => {});
    return () => { live = false; };
  }, [openRep]);

  useEffect(() => {
    if (tab === 'progress' && !progress) {
      getTrainingProgress().then(setProgress).catch(() => toast.error('Could not load progress'));
    }
  }, [tab, progress]);

  async function showRep(r) {
    if (openRep === r.user_id) { setParam({ rep: null }); return; }
    setParam({ rep: r.user_id }); setRepAttempts(null);
    try { setRepAttempts(await getRepAttempts(r.user_id)); }
    catch { toast.error('Could not load that rep'); }
  }

  async function addCourse() {
    if (!courseForm.title.trim()) return toast.error('A course needs a name');
    try { await createCourse(courseForm); toast.success('Course created');
      setCourseForm({ title: '', description: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not create it'); }
  }
  async function saveCourse() {
    if (!editCourse?.title.trim()) return toast.error('A course needs a name');
    try { await updateCourse(editCourse.id, editCourse); toast.success('Saved'); setEditCourse(null); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not save it'); }
  }
  async function removeCourse(c) {
    const mine = levels.filter(l => l.course_id === c.id);
    if (mine.length) {
      const others = courses.filter(x => x.id !== c.id);
      if (!others.length) return toast.error(`"${c.title}" holds ${mine.length} steps and it is your only course. Create another first.`);
      const to = window.prompt(
        `"${c.title}" holds ${mine.length} step${mine.length === 1 ? '' : 's'}. They will be MOVED, not deleted.\n\n` +
        others.map(o => `${o.id} = ${o.title}`).join('\n') +
        `\n\nType the number of the course to move them into:`);
      if (!to) return;
      try { await deleteCourse(c.id, parseInt(to, 10)); toast.success('Course removed, steps moved'); load(); }
      catch (e) { toast.error(e.response?.data?.error || 'Could not remove it'); }
      return;
    }
    if (!window.confirm(`Remove the course "${c.title}"? It has no steps in it.`)) return;
    try { await deleteCourse(c.id); toast.success('Course removed'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not remove it'); }
  }
  async function moveLevel(levelId, courseId) {
    try { await setLevelCourse(levelId, courseId ? parseInt(courseId, 10) : null); toast.success('Step moved'); load(); }
    catch { toast.error('Could not move that step'); }
  }

  async function addLevel() {
    if (!levelForm.title) return toast.error('A step needs a title');
    try { await createLevel(levelForm); toast.success('Step added'); setLevelForm(f => ({ title: '', description: '', passing_score: 80, sort_order: 0, course_id: f.course_id })); load(); }
    catch (e) { toast.error('Failed'); }
  }
  async function removeLevel(id) {
    const lvl = levels.find(l => l.id === id);
    const nLes = lvl?.lessons?.length || 0;
    const warn = `Delete "${lvl?.title || 'this step'}"?\n\n` +
      (nLes ? `Its ${nLes} lesson${nLes === 1 ? '' : 's'}${lvl?.quiz ? ' and its quiz' : ''} will be deleted too.\n` : '') +
      'This cannot be undone.';
    if (!window.confirm(warn)) return;
    try { await deleteLevel(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  }
  async function addLesson() {
    if (!lessonForm.level_id || !lessonForm.title) return toast.error('Pick a step and enter a title');
    const fd = new FormData();
    Object.entries(lessonForm).forEach(([k, v]) => { if (k === 'audioFile') { if (v) fd.append('audio', v); } else fd.append(k, v); });
    try { await createLesson(fd); toast.success('Lesson added'); setLessonForm({ level_id: lessonForm.level_id, title: '', type: 'text', content: '', audio_url: '', cheatsheet: '', sort_order: 0, audioFile: null }); load(); }
    catch (e) { toast.error('Failed'); }
  }
  async function removeLesson(id) {
    if (!window.confirm('Delete this lesson?')) return;
    try { await deleteLesson(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  }
  async function addQuiz() {
    if (!quizForm.level_id) return toast.error('Pick a step for the quiz');
    if (quizForm.questions.some(q => !q.prompt)) return toast.error('Every question needs a prompt');
    const payload = { level_id: quizForm.level_id, title: quizForm.title, scope: 'level', passing_score: quizForm.passing_score,
      questions: quizForm.questions.map(q => ({ type: q.type, prompt: q.prompt,
        options: q.type === 'mc' ? q.options.filter(Boolean) : [],
        correct_answer: q.correct_answer,
        accepted: q.type === 'text' ? q.accepted.split(',').map(s => s.trim()).filter(Boolean) : undefined })) };
    try { await createQuiz(payload); toast.success('Quiz saved'); setQuizForm({ level_id: quizForm.level_id, title: 'Step Quiz', passing_score: 80, questions: [blankQ()] }); load(); }
    catch (e) { toast.error('Failed'); }
  }

  const setQ = (i, patch) => setQuizForm(f => ({ ...f, questions: f.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) }));
  const card = { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 18, border: '1px solid #eee', maxWidth: 760 };
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 7, fontSize: 14, marginBottom: 10 };
  const lbl = { fontSize: 12, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 };

  if (loading) return <><div className="page-header"><h2>🎓 University Admin</h2></div><div className="page-body"><div style={{ padding: 30, color: '#aaa' }}>Loading…</div></div></>;

  const TABS = [
    ['overview', '🏠 Overview', '#0d1f3b'],
    ['courses',  '📚 Courses', '#1a3f7b'],
    ['progress', '📊 Who\u2019s Passing', '#7A5BA8'],
    ['levels',   '🎓 Steps', '#2c508b'],
    ['lessons',  '📄 Lessons', '#0277bd'],
    ['quizzes',  '📝 Quizzes', '#b8860b'],
    ['videos',   '🎬 Videos', '#8e24aa'],
  ];
  const activeColor = TABS.find(t => t[0] === tab)[2];

  const Banner = ({ color, icon, title, text }) => (
    <div style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)`, color: '#fff', borderRadius: 12, padding: '16px 20px', marginBottom: 18, maxWidth: 760 }}>
      <div style={{ fontSize: 17, fontWeight: 800 }}>{icon} {title}</div>
      <div style={{ fontSize: 13, opacity: .92, marginTop: 4 }}>{text}</div>
    </div>
  );

  return (
    <>
      <div className="page-header"><h2>🎓 University Admin</h2><div style={{ fontSize: 13, color: '#888' }}>Build the Amanda's University content</div></div>
      <div className="page-body">
        {/* tab bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {TABS.map(([k, l, c]) => (
            <button key={k} onClick={() => setTab(k)} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14,
              background: tab === k ? c : '#fff', color: tab === k ? '#fff' : '#555', boxShadow: tab === k ? '0 3px 10px rgba(0,0,0,0.18)' : '0 1px 3px rgba(0,0,0,0.08)' }}>{l}</button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <>
            <Banner color="#0d1f3b" icon="🏠" title="How Amanda's University works"
              text="Reps move through Steps in order. A step unlocks when they complete ALL its lessons AND pass its quiz. Build content in 3 steps:" />
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', maxWidth: 760, marginBottom: 20 }}>
              {[['1', 'Create a Step', 'e.g. "Step 3: Set the Stage" — set its passing score', '#2c508b', 'levels'],
                ['2', 'Add Lessons to it', 'Text or audio lessons, each with an optional cheat-sheet', '#0277bd', 'lessons'],
                ['3', 'Add the Step Quiz', 'Passing it (plus finishing lessons) unlocks the next step', '#b8860b', 'quizzes']].map(([n, t, d, c, goto]) => (
                <div key={n} onClick={() => setTab(goto)} style={{ flex: '1 1 200px', background: '#fff', borderRadius: 12, padding: 16, borderTop: `4px solid ${c}`, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: c, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, marginBottom: 8 }}>{n}</div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{t}</div>
                  <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>{d}</div>
                </div>
              ))}
            </div>
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ margin: 0, color: GREEN }}>📋 Current Curriculum</h3>
                <button className="btn btn-outline btn-sm"
                  onClick={async () => { try { const r = await importCprSteps(); toast.success(r.message || 'Done'); load(); } catch { toast.error('Failed'); } }}>
                  🔄 Refresh Amanda's University content
                </button>
              </div>
              {levels.length === 0 && <div style={{ color: '#999' }}>Nothing yet — add a step in the Steps tab, or load sample content:</div>}
              {levels.length === 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button className="btn btn-outline btn-sm"
                    onClick={async () => { try { const r = await seedTraining(); toast.success(r.message || 'Seeded'); load(); } catch { toast.error('Failed'); } }}>
                    ✨ Load starter content (2 sample levels)
                  </button>
                </div>
              )}
              {levels.map((l, i) => (
                <div key={l.id} style={{ borderBottom: '1px solid #f0f0f0', padding: '10px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#2c508b', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>{i + 1}</span>
                    <strong>{l.title}</strong>
                    <span style={{ fontSize: 12, color: '#999' }}>(pass {l.passing_score}%)</span>
                    <span style={{ flex: 1 }} />
                    <button className="btn btn-ghost btn-sm" style={{ color: '#c62828' }} onClick={() => removeLevel(l.id)}>Delete</button>
                  </div>
                  <div style={{ fontSize: 13, color: '#777', paddingLeft: 38 }}>
                    {l.lessons.map(les => (
                      <div key={les.id} style={{ padding: '3px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ cursor: 'pointer', flex: 1 }} onClick={() => { setPreview(preview === les.id ? null : les.id); if (editId === les.id) setEditId(null); }}>
                            {les.type === 'audio' ? '🎧' : '📄'} {les.title}
                            <span style={{ color: '#0277bd', fontSize: 11, marginLeft: 8 }}>{preview === les.id ? 'hide' : 'view'}</span>
                          </span>
                          {editId !== les.id && <button className="btn btn-ghost btn-sm" style={{ color: '#0277bd', padding: '0 6px' }} onClick={() => startEdit(les)}>✏️ edit</button>}
                          <button className="btn btn-ghost btn-sm" style={{ color: '#c62828', padding: '0 6px' }} onClick={() => removeLesson(les.id)}>✕</button>
                        </div>
                        {preview === les.id && editId !== les.id && (
                          <div style={{ background: '#f7f8f9', border: '1px solid #e6e8eb', borderRadius: 8, padding: 12, margin: '6px 0', color: '#333' }}>
                            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6 }}>{les.content || '(no content)'}</div>
                            {les.cheatsheet && (
                              <div style={{ marginTop: 10, background: '#fff8e6', border: '1px solid #ffe082', borderRadius: 6, padding: 10 }}>
                                <div style={{ fontWeight: 700, color: '#8a6d00', fontSize: 12, marginBottom: 4 }}>⚡ Cheat-sheet</div>
                                <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#6b5500' }}>{les.cheatsheet}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {editId === les.id && (
                          <div style={{ background: '#f4f8ff', border: '1px solid #cfe0f5', borderRadius: 8, padding: 12, margin: '6px 0' }}>
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Title</label>
                            <input value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{ width: '100%', padding: 7, borderRadius: 6, border: '1px solid #ccc', marginBottom: 8, fontSize: 13 }} />
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Lesson content</label>
                            <textarea value={editContent} onChange={e => setEditContent(e.target.value)} style={{ width: '100%', minHeight: 220, padding: 8, borderRadius: 6, border: '1px solid #ccc', marginBottom: 8, fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit' }} />
                            <label style={{ fontSize: 11, fontWeight: 700, color: '#8a6d00' }}>⚡ Cheat-sheet</label>
                            <textarea value={editCheat} onChange={e => setEditCheat(e.target.value)} style={{ width: '100%', minHeight: 90, padding: 8, borderRadius: 6, border: '1px solid #ffe082', background: '#fffdf5', marginBottom: 10, fontSize: 12, fontFamily: 'inherit' }} />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => saveEdit(les)}>{saving ? 'Saving…' : '💾 Save'}</button>
                              <button className="btn btn-outline btn-sm" disabled={saving} onClick={() => setEditId(null)}>Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {l.lessons.length === 0 && <div style={{ color: '#bbb', padding: '3px 0' }}>No lessons yet → add in the Lessons tab</div>}
                    {l.quiz ? <div style={{ padding: '3px 0' }}>📝 {l.quiz.title} <button className="btn btn-ghost btn-sm" style={{ color: '#c62828', padding: '0 6px' }} onClick={() => deleteQuiz(l.quiz.id).then(load)}>✕</button></div>
                      : <div style={{ color: '#bbb', padding: '3px 0' }}>No quiz yet → add in the Quizzes tab</div>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── LEVELS ── */}
        {tab === 'progress' && (
          <>
            <Banner color="#7A5BA8" icon="📊" title="Who’s passing"
              text="Every rep: how far through the course they are, how they scored, which videos they watched and which guides they opened. A rep cannot open the next step until they pass the current step’s quiz, so the progress here is real, not self-reported." />

            {!progress ? <div style={{ padding: 24, color: '#999' }}>Loading…</div> : (<>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                {[['People', progress.summary.people],
                  ['Started', progress.summary.started],
                  ['Finished', progress.summary.finished],
                  ['Not started', progress.summary.not_started],
                  ['Average score', progress.summary.avg_score == null ? '—' : progress.summary.avg_score + '%']
                ].map(([k, v]) => (
                  <div key={k} style={{ flex: '1 1 130px', background: '#fff', border: '1px solid #e7e9eb',
                    borderRadius: 12, padding: '12px 15px' }}>
                    <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: .5, color: '#8b9097', fontWeight: 700 }}>{k}</div>
                    <div style={{ fontSize: 21, fontWeight: 800, color: GREEN }}>{v}</div>
                  </div>
                ))}
              </div>

              {progress.reps.map(r => (
                <div key={r.user_id} style={{ ...card, padding: 0, overflow: 'hidden' }}>
                  <button onClick={() => showRep(r)} style={{ display: 'flex', alignItems: 'center', gap: 14,
                    width: '100%', textAlign: 'left', font: 'inherit', background: 'none', border: 'none',
                    padding: '15px 18px', cursor: 'pointer' }}>
                    <span style={{ flex: '1 1 150px', minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 800, fontSize: 15 }}>{r.username}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: '#8b9097', marginTop: 2 }}>
                        {r.started
                          ? `${r.quizzes_taken} quiz${r.quizzes_taken === 1 ? '' : 'zes'} · ${r.attempts} attempt${r.attempts === 1 ? '' : 's'}`
                          : 'Has not started'}
                      </span>
                    </span>

                    <span style={{ flex: '2 1 200px', minWidth: 130 }}>
                      <span style={{ display: 'block', height: 8, background: '#eef0f2', borderRadius: 99, overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: r.percent_complete + '%',
                          background: r.percent_complete === 100 ? '#1a3f7b' : GOLD, borderRadius: 99,
                          transition: 'width .8s cubic-bezier(.2,.8,.3,1)' }} />
                      </span>
                      <span style={{ display: 'block', fontSize: 11.5, color: '#8b9097', marginTop: 5, fontWeight: 600 }}>
                        {r.steps_passed} of {r.steps_total} steps · {r.percent_complete}%
                      </span>
                    </span>

                    <span style={{ textAlign: 'center', minWidth: 62 }}>
                      <span style={{ display: 'block', fontSize: 15, fontWeight: 700,
                        color: r.videos_watched ? GREEN : '#c3c6cc' }}>{r.videos_watched || 0}</span>
                      <span style={{ display: 'block', fontSize: 10.5, color: '#8b9097' }}>
                        video{r.videos_watched === 1 ? '' : 's'}
                      </span>
                    </span>
                    <span style={{ textAlign: 'center', minWidth: 62 }}>
                      <span style={{ display: 'block', fontSize: 15, fontWeight: 700,
                        color: r.guides_opened ? GREEN : '#c3c6cc' }}>{r.guides_opened || 0}</span>
                      <span style={{ display: 'block', fontSize: 10.5, color: '#8b9097' }}>guides</span>
                    </span>

                    <span style={{ textAlign: 'right', minWidth: 78 }}>
                      <span style={{ display: 'block', fontSize: 17, fontWeight: 800,
                        color: r.avg_score == null ? '#c3c6cc' : r.avg_score >= 80 ? '#1a3f7b' : '#b4453a' }}>
                        {r.avg_score == null ? '—' : r.avg_score + '%'}
                      </span>
                      <span style={{ display: 'block', fontSize: 10.5, color: '#8b9097' }}>avg score</span>
                    </span>

                    <span style={{ textAlign: 'right', minWidth: 66 }}>
                      <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: '#4c515a' }}>
                        {r.percentile == null ? '—' : r.percentile + 'th'}
                      </span>
                      <span style={{ display: 'block', fontSize: 10.5, color: '#8b9097' }}>percentile</span>
                    </span>
                    <span style={{ color: '#b9bec6', fontSize: 18 }}>{openRep === r.user_id ? '⌃' : '⌄'}</span>
                  </button>

                  {openRep === r.user_id && (
                    <div style={{ borderTop: '1px solid #eef0f2', padding: '14px 18px', background: '#fafbfc' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase',
                        letterSpacing: .5, color: '#8b9097', marginBottom: 9 }}>Step by step</div>
                      {r.steps.map(st => (
                        <div key={st.level_id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                          padding: '7px 0', fontSize: 13, borderBottom: '1px solid #f2f3f5' }}>
                          <span style={{ width: 18 }}>{st.complete ? '✅' : st.best_score != null ? '❌' : '⬜'}</span>
                          <span style={{ flex: 1 }}>{st.title}</span>
                          <span style={{ color: '#8b9097', fontSize: 12 }}>
                            {st.best_score == null ? (st.lessons_done ? 'lesson read, no quiz yet' : 'not started')
                              : `best ${st.best_score}% · ${st.attempts} attempt${st.attempts === 1 ? '' : 's'}`}
                          </span>
                        </div>
                      ))}
                      {(r.activity || []).length > 0 && (<>
                        <div style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase',
                          letterSpacing: .5, color: '#8b9097', margin: '14px 0 8px' }}>Videos &amp; guides opened</div>
                        {r.activity.map(a => (
                          <div key={a.kind + a.ref} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '5px 0' }}>
                            <span style={{ width: 18 }}>{a.kind === 'video' ? '🎬' : '🔍'}</span>
                            <span style={{ flex: 1 }}>{a.label || a.ref}</span>
                            <span style={{ color: '#8b9097' }}>
                              {a.opens}×{a.kind === 'video' && a.seconds ? ` · ${Math.round(a.seconds / 60)} min` : ''}
                            </span>
                            <span style={{ color: '#9a9fa6', minWidth: 92, textAlign: 'right' }}>
                              {(a.last || '').slice(0, 10)}
                            </span>
                          </div>
                        ))}
                      </>)}

                      <div style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase',
                        letterSpacing: .5, color: '#8b9097', margin: '14px 0 8px' }}>Every quiz attempt</div>
                      {!repAttempts ? <div style={{ color: '#aaa', fontSize: 12.5 }}>Loading…</div>
                        : repAttempts.attempts.length === 0 ? <div style={{ color: '#8b9097', fontSize: 12.5 }}>No quiz attempts yet.</div>
                        : repAttempts.attempts.map(a => (
                          <div key={a.id} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '5px 0' }}>
                            <span style={{ width: 18 }}>{a.passed ? '✅' : '❌'}</span>
                            <span style={{ flex: 1 }}>{a.level_title || a.quiz_title}</span>
                            <span style={{ fontWeight: 700, color: a.passed ? '#1a3f7b' : '#b4453a' }}>{a.score}%</span>
                            <span style={{ color: '#9a9fa6', minWidth: 76, textAlign: 'right' }}>
                              try {a.attempt_number}
                            </span>
                            <span style={{ color: '#9a9fa6', minWidth: 92, textAlign: 'right' }}>
                              {(a.created_at || '').slice(0, 10)}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </>)}
          </>
        )}

        {tab === 'courses' && (
          <>
            <Banner color="#1a3f7b" icon="📚" title="Courses"
              text="A course is a group of steps — for example the in-home sales process, or roofing product knowledge. Reps see one card per course and work through its steps in order. Progress is tracked per course, so finishing one does not unlock another." />

            <div style={card}>
              <label style={lbl}>Course name</label>
              <input style={inp} value={courseForm.title}
                onChange={e => setCourseForm(f => ({ ...f, title: e.target.value }))}
                placeholder='e.g. "Roofing Product Knowledge"' />
              <label style={lbl}>Short description (reps see this)</label>
              <input style={inp} value={courseForm.description}
                onChange={e => setCourseForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What this course covers" />
              <button className="btn btn-primary" onClick={addCourse}>＋ Create course</button>
            </div>

            {courses.map(c => {
              const mine = levels.filter(l => l.course_id === c.id);
              const isEditing = editCourse?.id === c.id;
              return (
                <div key={c.id} style={{ ...card, borderLeft: '4px solid #1a3f7b' }}>
                  {isEditing ? (
                    <>
                      <label style={lbl}>Course name</label>
                      <input style={inp} value={editCourse.title}
                        onChange={e => setEditCourse(f => ({ ...f, title: e.target.value }))} />
                      <label style={lbl}>Description</label>
                      <input style={inp} value={editCourse.description || ''}
                        onChange={e => setEditCourse(f => ({ ...f, description: e.target.value }))} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary btn-sm" onClick={saveCourse}>Save</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditCourse(null)}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: GREEN }}>{c.title}</div>
                        {c.description && <div style={{ fontSize: 12.5, color: '#7c818a', marginTop: 3 }}>{c.description}</div>}
                        <div style={{ fontSize: 12, color: '#9a9fa6', marginTop: 6 }}>
                          {mine.length} step{mine.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => setEditCourse({ id: c.id, title: c.title, description: c.description })}>Rename</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: '#b4453a' }}
                        onClick={() => removeCourse(c)}>Remove</button>
                    </div>
                  )}

                  {mine.length > 0 && (
                    <div style={{ marginTop: 14, borderTop: '1px solid #eef0f2', paddingTop: 12 }}>
                      {mine.map(l => (
                        <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', fontSize: 13.5 }}>
                          <span style={{ color: '#9a9fa6', fontSize: 12, minWidth: 22 }}>{l.sort_order || '–'}</span>
                          <span style={{ flex: 1 }}>{l.title}</span>
                          <select value={c.id} onChange={e => moveLevel(l.id, e.target.value)}
                            style={{ ...inp, width: 'auto', marginBottom: 0, padding: '6px 9px', fontSize: 12.5 }}>
                            {courses.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {levels.some(l => !l.course_id) && (
              <div style={{ ...card, borderLeft: '4px solid #b8860b' }}>
                <div style={{ fontWeight: 800, color: '#8a6d00', marginBottom: 8 }}>Steps not in any course</div>
                <div style={{ fontSize: 12.5, color: '#7c818a', marginBottom: 10 }}>
                  Reps cannot see these. Put each one into a course.
                </div>
                {levels.filter(l => !l.course_id).map(l => (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', fontSize: 13.5 }}>
                    <span style={{ flex: 1 }}>{l.title}</span>
                    <select defaultValue="" onChange={e => moveLevel(l.id, e.target.value)}
                      style={{ ...inp, width: 'auto', marginBottom: 0, padding: '6px 9px', fontSize: 12.5 }}>
                      <option value="" disabled>Move into…</option>
                      {courses.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'levels' && (
          <>
            <Banner color="#2c508b" icon="🎓" title="Steps"
              text="A step is one stage of a course. Reps read its lesson, pass its quiz, then the next step unlocks." />
            <div style={card}>
              <label style={lbl}>Which course does this step belong to?</label>
              <select style={inp} value={levelForm.course_id}
                onChange={e => setLevelForm(f => ({ ...f, course_id: e.target.value }))}>
                <option value="">{courses.length ? 'Choose a course…' : 'No courses yet — create one first'}</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <label style={lbl}>Title</label>
              <input style={inp} value={levelForm.title} onChange={e => setLevelForm(f => ({ ...f, title: e.target.value }))} placeholder='e.g. "Step 3: Set the Stage"' />
              <label style={lbl}>Short description (reps see this)</label>
              <input style={inp} value={levelForm.description} onChange={e => setLevelForm(f => ({ ...f, description: e.target.value }))} placeholder="What this step teaches" />
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}><label style={lbl}>Passing score % (to unlock the next step)</label><input type="number" style={inp} value={levelForm.passing_score} onChange={e => setLevelForm(f => ({ ...f, passing_score: parseInt(e.target.value) || 80 }))} /></div>
                <div style={{ flex: 1 }}><label style={lbl}>Order (1 = first)</label><input type="number" style={inp} value={levelForm.sort_order} onChange={e => setLevelForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} /></div>
              </div>
              <button className="btn btn-primary" onClick={addLevel}>＋ Add Step</button>
            </div>
          </>
        )}

        {/* ── LESSONS ── */}
        {tab === 'lessons' && (
          <>
            <Banner color="#0277bd" icon="📄" title="Lessons"
              text="Lessons live inside a level. They can be text or audio. The optional cheat-sheet is the quick revision reps can open before a job." />
            <div style={card}>
              <label style={lbl}>① Which step does this lesson belong to?</label>
              <select style={inp} value={lessonForm.level_id} onChange={e => setLessonForm(f => ({ ...f, level_id: e.target.value }))}>
                <option value="">— pick a step —</option>
                {levels.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
              </select>
              <label style={lbl}>② Lesson title</label>
              <input style={inp} value={lessonForm.title} onChange={e => setLessonForm(f => ({ ...f, title: e.target.value }))} placeholder='e.g. "The First 30 Seconds"' />
              <label style={lbl}>③ Type</label>
              <select style={inp} value={lessonForm.type} onChange={e => setLessonForm(f => ({ ...f, type: e.target.value }))}>
                <option value="text">📄 Text lesson</option><option value="audio">🎧 Audio lesson</option>
              </select>
              {lessonForm.type === 'audio' && (
                <>
                  <label style={lbl}>Audio file (upload)</label>
                  <input type="file" accept="audio/*" style={inp} onChange={e => setLessonForm(f => ({ ...f, audioFile: e.target.files[0] }))} />
                  <label style={lbl}>…or audio/video link</label>
                  <input style={inp} value={lessonForm.audio_url} onChange={e => setLessonForm(f => ({ ...f, audio_url: e.target.value }))} placeholder="https://…" />
                </>
              )}
              <label style={lbl}>④ Lesson content {lessonForm.type === 'audio' ? '(optional notes)' : ''}</label>
              <textarea style={{ ...inp, minHeight: 110 }} value={lessonForm.content} onChange={e => setLessonForm(f => ({ ...f, content: e.target.value }))} placeholder="Write the lesson here…" />
              <label style={lbl}>⑤ Cheat-sheet (optional — short bullet reminders)</label>
              <textarea style={{ ...inp, minHeight: 70 }} value={lessonForm.cheatsheet} onChange={e => setLessonForm(f => ({ ...f, cheatsheet: e.target.value }))} placeholder="Quick key points…" />
              <button className="btn btn-primary" onClick={addLesson}>＋ Add Lesson</button>
            </div>
          </>
        )}

        {/* ── QUIZZES ── */}
        {tab === 'quizzes' && (
          <>
            <Banner color="#b8860b" icon="📝" title="Level Quizzes"
              text="Each level gets ONE quiz. Reps must pass it (after finishing all lessons) to unlock the next level. They get 3 attempts before a manager reset." />
            <div style={card}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 2 }}><label style={lbl}>① Which step is this quiz for?</label>
                  <select style={inp} value={quizForm.level_id} onChange={e => setQuizForm(f => ({ ...f, level_id: e.target.value }))}>
                    <option value="">— pick a step —</option>
                    {levels.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}><label style={lbl}>Pass %</label><input type="number" style={inp} value={quizForm.passing_score} onChange={e => setQuizForm(f => ({ ...f, passing_score: parseInt(e.target.value) || 80 }))} /></div>
              </div>
              <label style={lbl}>② Questions</label>
              {quizForm.questions.map((q, i) => (
                <div key={i} style={{ background: '#fffaf0', border: '1px solid #f0e0b8', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <select style={{ ...inp, marginBottom: 0, width: 150 }} value={q.type} onChange={e => setQ(i, { type: e.target.value })}>
                      <option value="mc">Multiple choice</option><option value="tf">True / False</option><option value="text">Short text</option>
                    </select>
                    <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder="Question prompt" value={q.prompt} onChange={e => setQ(i, { prompt: e.target.value })} />
                  </div>
                  {q.type === 'mc' && (
                    <>
                      {q.options.map((opt, oi) => (
                        <div key={oi} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'center' }}>
                          <input type="radio" name={`correct${i}`} checked={q.correct_answer === opt && opt !== ''} onChange={() => setQ(i, { correct_answer: opt })} title="Mark correct" />
                          <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder={`Option ${oi + 1}`} value={opt}
                            onChange={e => { const o = [...q.options]; o[oi] = e.target.value; setQ(i, { options: o, correct_answer: q.correct_answer === opt ? e.target.value : q.correct_answer }); }} />
                        </div>
                      ))}
                      <button className="btn btn-ghost btn-sm" onClick={() => setQ(i, { options: [...q.options, ''] })}>+ option</button>
                      <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>● tick the correct option</span>
                    </>
                  )}
                  {q.type === 'tf' && (
                    <select style={inp} value={q.correct_answer} onChange={e => setQ(i, { correct_answer: e.target.value })}>
                      <option value="">— correct answer —</option><option value="true">True</option><option value="false">False</option>
                    </select>
                  )}
                  {q.type === 'text' && (
                    <input style={inp} placeholder="Accepted answers, comma-separated (e.g. hello, hi)" value={q.accepted} onChange={e => setQ(i, { accepted: e.target.value })} />
                  )}
                  {quizForm.questions.length > 1 && <button className="btn btn-ghost btn-sm" style={{ color: '#c62828' }} onClick={() => setQuizForm(f => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }))}>Remove question</button>}
                </div>
              ))}
              <button className="btn btn-outline btn-sm" onClick={() => setQuizForm(f => ({ ...f, questions: [...f.questions, blankQ()] }))}>+ Add question</button>
              <div style={{ marginTop: 12 }}><button className="btn btn-primary" onClick={addQuiz}>💾 Save Quiz</button></div>
            </div>
          </>
        )}

        {/* ── VIDEOS ── */}
        {tab === 'videos' && (
          <>
            <Banner color="#8e24aa" icon="🎬" title="Video Library"
              text='Sections group related videos — e.g. "Ryan Full TC Pitch". Reps watch them in the Video Library tab. Upload files from your computer or paste YouTube/Drive links (they play embedded).' />
            <div style={card}>
              <label style={lbl}>① Create a section</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder='e.g. "Ryan Full TC Pitch"' value={sectionTitle} onChange={e => setSectionTitle(e.target.value)} />
                <button className="btn btn-primary" onClick={addSection}>＋ Add Section</button>
              </div>
              {videoSections.map(sec => (
                <div key={sec.id} style={{ borderBottom: '1px solid #f0f0f0', padding: '8px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong>🎬 {sec.title}</strong>
                    <span style={{ fontSize: 12, color: '#999' }}>({sec.videos.length})</span>
                    <span style={{ flex: 1 }} />
                    <button className="btn btn-ghost btn-sm" style={{ color: '#c62828' }} onClick={() => removeSection(sec.id)}>Delete section</button>
                  </div>
                  <div style={{ fontSize: 13, color: '#777', paddingLeft: 16 }}>
                    {sec.videos.map(v => (
                      <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                        <span>▶ {v.title} {v.url && <em style={{ color: '#aaa' }}>(link)</em>}</span>
                        <button className="btn btn-ghost btn-sm" style={{ color: '#c62828', padding: '0 6px' }} onClick={() => removeVideo(v.id)}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '16px 0' }} />
              <label style={lbl}>② Add a video to a section</label>
              <select style={inp} value={videoForm.section_id} onChange={e => setVideoForm(f => ({ ...f, section_id: e.target.value }))}>
                <option value="">— pick a section —</option>
                {videoSections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
              <label style={lbl}>Video title</label>
              <input style={inp} value={videoForm.title} onChange={e => setVideoForm(f => ({ ...f, title: e.target.value }))} placeholder='e.g. "Full pitch — part 1"' />
              <label style={lbl}>Video file (from your computer)</label>
              <input type="file" accept="video/*" style={inp} onChange={e => setVideoForm(f => ({ ...f, file: e.target.files[0] }))} />
              <label style={lbl}>…or paste a YouTube / Google Drive link (plays embedded)</label>
              <input style={inp} value={videoForm.url} onChange={e => setVideoForm(f => ({ ...f, url: e.target.value }))} placeholder="https://…" />
              {uploadPct !== null ? (
                <div style={{ margin: '6px 0 10px' }}>
                  <div style={{ height: 10, background: '#eee', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: uploadPct + '%', height: '100%', background: '#8e24aa', transition: 'width .2s' }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>Uploading… {uploadPct}%</div>
                </div>
              ) : (
                <button className="btn btn-primary" onClick={addVideo}>＋ Add Video</button>
              )}
              <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>Drive link tip: the file's sharing must be set to "Anyone with the link" for reps to watch it.</div>
            </div>
          </>
        )}
      </div>
    </>
  );
}